import { Text } from "@chakra-ui/react";
import { type ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import { type BuyerBidV2DetailDto } from "../../../../autogen/bff-api";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { verdictReopensDecision, type BidDecision } from "./decidedBidEdit";
import { useBidQualificationDecision } from "./useBidQualificationDecision";
import { useBidRejectionDecision } from "./useBidRejectionDecision";

export interface StandingVerdictGuard {
  changeVerdict: (verdict: boolean | null, applyToReview: () => void) => void;
  modal: ReactElement;
}

const ReopenWarning = ({ standingVerdict }: { standingVerdict: BidDecision }) => {
  const { t } = useTranslation();
  return (
    <Text>
      <Trans
        i18nKey="The qualification of this supplier is <bold>{{state}}</bold>. If you change the answer, the status goes back to not assessed. Do you want to continue?"
        values={{ state: standingVerdict === "REJECTED" ? t("Rejected") : t("Qualified") }}
        components={{ bold: <Text as="span" fontWeight="bold" /> }}
      />
    </Text>
  );
};

export const useStandingVerdictGuard = (detail: BuyerBidV2DetailDto): StandingVerdictGuard => {
  const { t } = useTranslation();
  const { show, modal } = useConfirmationModal();
  const qualification = useBidQualificationDecision(detail);
  const rejection = useBidRejectionDecision(detail);

  const standingVerdict: BidDecision = rejection.isRejected ? "REJECTED" : qualification.decision ? "QUALIFIED" : null;

  const clearStandingVerdict = (): Promise<boolean> => {
    if (rejection.isRejected) return rejection.revoke();
    if (qualification.decision) return qualification.decide(false);
    return Promise.resolve(true);
  };

  const changeVerdict = (verdict: boolean | null, applyToReview: () => void) => {
    if (!verdictReopensDecision(standingVerdict, verdict)) {
      applyToReview();
      return;
    }

    show({
      title: String(t("Reopen the decision?")),
      description: <ReopenWarning standingVerdict={standingVerdict} />,
      confirmButtonText: String(t("Yes")),
      cancelButtonText: String(t("No")),
      confirmButtonColor: "red",
      size: "lg",
      onConfirmed: async () => {
        if (await clearStandingVerdict()) applyToReview();
      },
    });
  };

  return { changeVerdict, modal };
};
